import DotSolarMap from '@/components/map/DotSolarMap.js';
import { logModuleListDebug, summarizeDefectModuleLinkage } from '@/features/defect-drawer/utils/moduleListSyncDebug';
import { moduleDefectApi } from '@/apis/module.defect.api';
import { invalidateModuleDefectDetailCache } from '@/utils/moduleDefectDetailCache.js';
import { resolveThermalImageForDefectDialog } from './resolveThermalImageForDefectDialog.js';
import { buildModuleDefectNavContext } from './moduleDefectNavigation.js';

/** @typedef {'image'|'module'} DefectDialogNavigationMode */

/**
 * 결함 수정 모달 열기 (대시보드·패널 플래닝 공통)
 * @param {import('vuex').Store} store
 * @param {object} defect
 * @param {{ navigationMode?: DefectDialogNavigationMode, moduleDefectNavList?: object[], moduleDefectNavIndex?: number, allModuleDefects?: object[], moduleSeqFilter?: number|string|null }} [options]
 */
export async function openDefectInDialog(store, defect, options = {}) {
  const thermalImageId =
    defect.solarThermalImageId || defect.thermal_img_seq || defect.thermalImgSeq;

  if (!thermalImageId) {
    console.warn('[DefectFeature] thermalImageId 없음');
    return Promise.resolve();
  }

  const thermalImage = await resolveThermalImageForDefectDialog(store, thermalImageId);
  if (!thermalImage) {
    console.warn('[DefectFeature] 열화상 객체를 찾지 못함:', thermalImageId);
    return Promise.resolve();
  }

  const defectId = defect.seq || defect.id;
  const navigationMode = options.navigationMode || 'image';

  if (navigationMode === 'module') {
    const nav =
      options.moduleDefectNavList != null
        ? {
            list: options.moduleDefectNavList,
            index:
              options.moduleDefectNavIndex != null
                ? options.moduleDefectNavIndex
                : buildModuleDefectNavContext(
                    options.allModuleDefects || [],
                    defect,
                    options.moduleSeqFilter
                  ).index
          }
        : buildModuleDefectNavContext(
            options.allModuleDefects || [],
            defect,
            options.moduleSeqFilter
          );

    return store.dispatch('defect/openDefectFromModuleDrawer', {
      thermalImage,
      defectId,
      moduleDefectNavList: nav.list,
      moduleDefectNavIndex: nav.index
    });
  }

  return store.dispatch('defect/openDefectFromList', {
    thermalImage,
    defectId,
    navigationMode: 'image'
  });
}

export { buildModuleDefectNavContext };

/** 결함 삭제 */
export async function deleteDefectById(seq, api = moduleDefectApi) {
  return api.deleteDefect(seq);
}

/**
 * 낙관적 결함 삭제 — 목록·지도에서 즉시 제거 후 API 호출, 실패 시 롤백
 * @param {import('vuex').Store} store
 * @param {object} defect
 * @param {{ reportId?: number|string, thermalImageId?: number|string, eventBus?: object, successMessage?: string }} [options]
 */
export async function optimisticDeleteDefect(store, defect, options = {}) {
  const seq = defect?.seq;
  if (seq == null) {
    return false;
  }

  const rows = store.state.dashboard?.wfsDefectRows || [];
  const removedIndex = rows.findIndex(d => Number(d.seq) === Number(seq));
  const removedRow = removedIndex >= 0 ? { ...rows[removedIndex] } : null;

  if (removedRow) {
    logModuleListDebug('delete.optimistic', {
      defectSeq: seq,
      removedRow: {
        solarModuleSeq: removedRow.solarModuleSeq,
        moduleId: removedRow.moduleId,
        arrayId: removedRow.arrayId,
        stringId: removedRow.stringId
      },
      beforeLinkage: summarizeDefectModuleLinkage(rows),
      drawerOpen: store.state.dashboard?.defectDrawerOpen ?? false
    });
    store.commit('dashboard/removeWfsDefectRow', seq);
    logModuleListDebug('delete.afterRemove', {
      afterLinkage: summarizeDefectModuleLinkage(store.state.dashboard?.wfsDefectRows || []),
      hierarchyKept:
        store.state.dashboard?.siteDesignModuleHierarchy?.length ?? 0
    });
  }
  DotSolarMap.removeDefectMarkersBySeq(seq);

  try {
    await deleteDefectById(seq);
    invalidateModuleDefectDetailCache(seq);

    if (options.eventBus) {
      options.eventBus.$emit('alert-on', {
        on: true,
        message: options.successMessage || '삭제되었습니다.',
        status: 'success'
      });
    }

    return true;
  } catch (error) {
    if (removedRow) {
      store.commit('dashboard/insertWfsDefectRow', { row: removedRow, index: removedIndex });
    }
    throw error;
  }
}

/** 대시보드 지도 이동 이벤트 */
export function emitDefectFlyTo(store, defect) {
  if (defect.lng == null || defect.lat == null) {
    console.log('[FLYTO-DEBUG] ② emitDefectFlyTo 중단 — 좌표 없음', {
      seq: defect?.seq,
      solarModuleSeq: defect?.solarModuleSeq,
      lng: defect?.lng,
      lat: defect?.lat,
      reason: 'lng/lat null → 지도 이동 안 함 (WFS defect_location null 추정)'
    });
    return;
  }
  console.log('[FLYTO-DEBUG] ② emitDefectFlyTo 발행', {
    seq: defect?.seq,
    solarModuleSeq: defect?.solarModuleSeq,
    lng: defect.lng,
    lat: defect.lat
  });
  store.commit('dashboard/setDefectFlyTarget', { ...defect });
}
